import { useCallback, useRef } from 'react';

/**
 * Simple signal manager for video viewport coordination.
 *
 * This replaces the complex PlaybackManager that was designed for STACK viewports
 * (frame-by-frame playback with manual animation loops). Since all multi-frame
 * DICOMs now use native VIDEO viewports, we only need:
 *
 * 1. Pause signal - to pause all videos when measurement tools are activated
 * 2. Toggle signal - for spacebar to toggle all videos play/pause
 *
 * Video playback is handled entirely by the browser's native <video> element.
 */
export function useVideoSignalManager() {
  const pauseSubscribersRef = useRef<Set<() => void>>(new Set());
  const toggleSubscribersRef = useRef<Set<() => void>>(new Set());

  /**
   * Subscribe to video pause signal.
   * Called when measurement tools (Length, Angle, Freehand ROI) are activated.
   * Returns unsubscribe function.
   */
  const subscribeToVideoPause = useCallback((callback: () => void) => {
    pauseSubscribersRef.current.add(callback);
    return () => {
      pauseSubscribersRef.current.delete(callback);
    };
  }, []);

  /**
   * Subscribe to video toggle signal.
   * Called when spacebar is pressed to toggle all videos.
   * Returns unsubscribe function.
   */
  const subscribeToVideoToggle = useCallback((callback: () => void) => {
    toggleSubscribersRef.current.add(callback);
    return () => {
      toggleSubscribersRef.current.delete(callback);
    };
  }, []);

  /**
   * Pause all video viewports.
   * Triggers pause callback for each subscribed video viewport.
   */
  const pauseAll = useCallback(() => {
    pauseSubscribersRef.current.forEach((callback) => {
      try {
        callback();
      } catch {
        // Subscriber error - ignore and continue
      }
    });
  }, []);

  /**
   * Toggle play/pause for all video viewports.
   * Each viewport handles its own play/pause state internally.
   */
  const togglePlayPauseAll = useCallback(() => {
    toggleSubscribersRef.current.forEach((callback) => {
      try {
        callback();
      } catch {
        // Subscriber error - ignore and continue
      }
    });
  }, []);

  return {
    subscribeToVideoPause,
    subscribeToVideoToggle,
    pauseAll,
    togglePlayPauseAll,
  };
}
